<script setup>
/* global axios */
// Kisi listesi gorunurluk ayari.
//
// Chatwoot sabit bir kural uyguluyordu: yalnizca e-posta / telefon /
// tanimlayici alani dolu kisiler listelenir. Sosyal medya kanallarinda bu
// kural gercek musterileri gizliyor. Kurali hesaba birakmak icin bu ekran.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const route = useRoute();

const yukleniyor = ref(true);
const kaydediliyor = ref(false);
const hata = ref('');
const oznitelikler = ref([]);
const gorunen = ref(0);
const toplam = ref(0);

const politika = ref({
  mod: 'iletisim_bilgisi',
  ozel: {
    eposta: true,
    telefon: true,
    tanimlayici: true,
    konusmasi_olanlar: false,
    oznitelikler: [],
    anahtar_kelimeler: [],
  },
});

const yeniKelime = ref('');

const uc = computed(() => `/api/v1/accounts/${route.params.accountId}/kisi-politikasi`);

const MODLAR = [
  { anahtar: 'iletisim_bilgisi', baslik: 'MOD_ILETISIM', aciklama: 'MOD_ILETISIM_ACIKLAMA' },
  { anahtar: 'konusmasi_olanlar', baslik: 'MOD_KONUSMA', aciklama: 'MOD_KONUSMA_ACIKLAMA' },
  { anahtar: 'hepsi', baslik: 'MOD_HEPSI', aciklama: 'MOD_HEPSI_ACIKLAMA' },
  { anahtar: 'ozel', baslik: 'MOD_OZEL', aciklama: 'MOD_OZEL_ACIKLAMA' },
];

const yukle = async () => {
  yukleniyor.value = true;
  hata.value = '';
  try {
    const { data } = await axios.get(uc.value);
    politika.value = data.politika;
    oznitelikler.value = data.oznitelikler || [];
    gorunen.value = data.gorunen;
    toplam.value = data.toplam;
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    yukleniyor.value = false;
  }
};

const kaydet = async () => {
  kaydediliyor.value = true;
  hata.value = '';
  try {
    const { data } = await axios.patch(uc.value, { politika: politika.value });
    politika.value = data.politika;
    gorunen.value = data.gorunen;
    toplam.value = data.toplam;
    useAlert(t('KISI_POLITIKASI.KAYDEDILDI'));
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    kaydediliyor.value = false;
  }
};

const ozniteligiDegistir = (anahtar, acik) => {
  const liste = new Set(politika.value.ozel.oznitelikler);
  if (acik) liste.add(anahtar);
  else liste.delete(anahtar);
  politika.value.ozel.oznitelikler = [...liste];
};

const kelimeEkle = () => {
  const k = yeniKelime.value.trim();
  if (!k) return;
  if (!politika.value.ozel.anahtar_kelimeler.includes(k)) {
    politika.value.ozel.anahtar_kelimeler.push(k);
  }
  yeniKelime.value = '';
};

const kelimeSil = k => {
  politika.value.ozel.anahtar_kelimeler =
    politika.value.ozel.anahtar_kelimeler.filter(x => x !== k);
};

onMounted(yukle);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-surface-1">
    <header class="px-6 py-4 border-b border-n-weak">
      <h1 class="text-lg font-medium text-n-slate-12">
        {{ t('KISI_POLITIKASI.BASLIK') }}
      </h1>
      <p class="mt-1 text-sm text-n-slate-11 max-w-2xl">
        {{ t('KISI_POLITIKASI.ACIKLAMA') }}
      </p>
    </header>

    <div v-if="yukleniyor" class="flex justify-center p-10">
      <Spinner class="text-n-brand" />
    </div>

    <div v-else class="flex flex-col gap-6 p-6 max-w-3xl">
      <p v-if="hata" class="text-sm text-n-ruby-11">{{ hata }}</p>

      <p class="text-sm text-n-slate-11">
        {{ t('KISI_POLITIKASI.SAYIM', { gorunen, toplam }) }}
      </p>

      <div class="flex flex-col gap-2">
        <label
          v-for="m in MODLAR"
          :key="m.anahtar"
          class="flex gap-3 p-4 border rounded-xl cursor-pointer transition-colors"
          :class="
            politika.mod === m.anahtar
              ? 'border-n-brand bg-n-alpha-1'
              : 'border-n-weak hover:border-n-slate-6'
          "
        >
          <input
            v-model="politika.mod"
            type="radio"
            :value="m.anahtar"
            class="mt-1"
          />
          <span class="flex flex-col gap-0.5">
            <strong class="text-sm text-n-slate-12">
              {{ t(`KISI_POLITIKASI.${m.baslik}`) }}
            </strong>
            <span class="text-sm text-n-slate-11">
              {{ t(`KISI_POLITIKASI.${m.aciklama}`) }}
            </span>
          </span>
        </label>
      </div>

      <div
        v-if="politika.mod === 'ozel'"
        class="flex flex-col gap-4 p-4 border rounded-xl border-n-weak"
      >
        <p class="m-0 text-sm text-n-slate-11">
          {{ t('KISI_POLITIKASI.OZEL_NOT') }}
        </p>

        <div class="flex flex-col gap-2">
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="politika.ozel.eposta" type="checkbox" />
            {{ t('KISI_POLITIKASI.OLCUT_EPOSTA') }}
          </label>
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="politika.ozel.telefon" type="checkbox" />
            {{ t('KISI_POLITIKASI.OLCUT_TELEFON') }}
          </label>
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="politika.ozel.tanimlayici" type="checkbox" />
            {{ t('KISI_POLITIKASI.OLCUT_TANIMLAYICI') }}
          </label>
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="politika.ozel.konusmasi_olanlar" type="checkbox" />
            {{ t('KISI_POLITIKASI.OLCUT_KONUSMA') }}
          </label>
        </div>

        <div v-if="oznitelikler.length" class="flex flex-col gap-2">
          <strong class="text-sm text-n-slate-12">
            {{ t('KISI_POLITIKASI.OLCUT_OZNITELIK') }}
          </strong>
          <label
            v-for="o in oznitelikler"
            :key="o.anahtar"
            class="flex items-center gap-2 text-sm text-n-slate-12"
          >
            <input
              type="checkbox"
              :checked="politika.ozel.oznitelikler.includes(o.anahtar)"
              @change="ozniteligiDegistir(o.anahtar, $event.target.checked)"
            />
            {{ o.ad }}
          </label>
        </div>

        <div class="flex flex-col gap-2">
          <strong class="text-sm text-n-slate-12">
            {{ t('KISI_POLITIKASI.OLCUT_KELIME') }}
          </strong>
          <p class="m-0 text-xs text-n-slate-11">
            {{ t('KISI_POLITIKASI.KELIME_UYARI') }}
          </p>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="k in politika.ozel.anahtar_kelimeler"
              :key="k"
              class="flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-12"
            >
              {{ k }}
              <button type="button" class="text-n-slate-11" @click="kelimeSil(k)">
                ×
              </button>
            </span>
          </div>
          <div class="flex gap-2">
            <input
              v-model="yeniKelime"
              type="text"
              class="!mb-0"
              :placeholder="t('KISI_POLITIKASI.KELIME_YER_TUTUCU')"
              @keyup.enter="kelimeEkle"
            />
            <Button variant="faded" :label="t('KISI_POLITIKASI.EKLE')" @click="kelimeEkle" />
          </div>
        </div>
      </div>

      <div>
        <Button
          :label="t('KISI_POLITIKASI.KAYDET')"
          :is-loading="kaydediliyor"
          @click="kaydet"
        />
      </div>
    </div>
  </div>
</template>
